import logger from '../../utils/logger';
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "../../style/ImagingModals.css";

const listOfRegions = [
  "Head & Neck",
  "Spine",
  "Upper Extremity",
  "Lower Extremity",
  "Chest / Thorax",
  "Abdomen / Pelvis",
  "Vascular",
  "Soft Tissue / Foreign Body",
  "Full Body",
];

const modalities = ["X-Ray", "DEXA", "MRI", "CT", "US"];

/**
 * Modal for adding a new custom imaging order (CPT code entry).
 *
 * Props:
 *  - isOpen              {boolean}   Whether the modal is visible
 *  - onClose             {function}  Called when the modal should close
 *  - onSuccess           {function}  Called after a successful save (triggers refetchCatalog)
 *  - preselectedRegion   {string}    Optional — pre-fills the region dropdown
 *  - preselectedModality {string}    Optional — pre-fills the modality dropdown
 */
const AddImagingOrderModal = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedRegion = "",
  preselectedModality = "",
  user = null,
}) => {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [region, setRegion] = useState(preselectedRegion);
  const [modality, setModality] = useState(preselectedModality);
  const [cptCode, setCptCode] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync preselected values when modal opens
  useEffect(() => {
    if (isOpen) {
      setRegion(preselectedRegion);
      setModality(preselectedModality);
    }
  }, [isOpen, preselectedRegion, preselectedModality]);

  if (!isOpen) return null;

  const resetForm = () => {
    setRegion(preselectedRegion);
    setModality(preselectedModality);
    setCptCode("");
    setName("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!region) {
      toast.warning("Please select a region");
      return;
    }
    if (!modality) {
      toast.warning("Please select a modality");
      return;
    }
    if (!cptCode.trim()) {
      toast.warning("Please enter a CPT code");
      return;
    }
    if (!name.trim()) {
      toast.warning("Please enter an exam name");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/customImagingOrder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          region,
          modality,
          cpt_code: cptCode.trim(),
          name: name.trim(),
          user,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.info("Imaging order submitted for admin approval");
        resetForm();
        onSuccess?.();
        onClose();
      } else {
        toast.error(data.msg || "Failed to add imaging order");
      }
    } catch (error) {
      logger.error("Error adding imaging order:", error);
      toast.error("Error adding imaging order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="im-modal-overlay" onClick={handleClose}>
      <div className="im-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <h3>Add Custom Imaging Order</h3>
          <button className="im-modal-close" onClick={handleClose} disabled={isSubmitting}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="im-modal-body">
            <div className="im-form-group">
              <label htmlFor="aio-region">Region</label>
              <select
                id="aio-region"
                className="im-select"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select Region</option>
                {listOfRegions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="im-form-group">
              <label htmlFor="aio-modality">Modality</label>
              <select
                id="aio-modality"
                className="im-select"
                value={modality}
                onChange={(e) => setModality(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select Modality</option>
                {modalities.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="im-form-group">
              <label htmlFor="aio-cpt">CPT Code</label>
              <input
                id="aio-cpt"
                type="text"
                className="im-input"
                value={cptCode}
                onChange={(e) => setCptCode(e.target.value)}
                placeholder="e.g. 73221"
                disabled={isSubmitting}
              />
            </div>

            <div className="im-form-group">
              <label htmlFor="aio-name">Exam Name</label>
              <input
                id="aio-name"
                type="text"
                className="im-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. MRI Right Shoulder w/o contrast"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="im-modal-footer">
            <button
              type="button"
              className="im-btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="im-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddImagingOrderModal;
